import { useEffect } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useThemedStyles } from '../theme/useThemedStyles';
import type { Theme } from '../theme/types';

const DISMISS_AFTER_MS = 4000;

interface UndoBannerProps {
  message: string;
  onUndo: () => void;
  onDismiss: () => void;
}

/**
 * A short-lived "<message> · Undo" bar pinned above the list's bottom edge.
 *
 * For changes that move an item out of the view the user acted in, where the
 * control they tapped is gone and so cannot reverse it. Re-keying the banner
 * (a new `message` or a parent `key`) restarts the timer.
 */
export default function UndoBanner({ message, onUndo, onDismiss }: UndoBannerProps) {
  const styles = useThemedStyles(createStyles);

  useEffect(() => {
    const timer = setTimeout(onDismiss, DISMISS_AFTER_MS);
    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  return (
    <View style={styles.banner} accessibilityLiveRegion="polite" testID="undo-banner">
      <Text style={styles.message} numberOfLines={1}>{message}</Text>
      <TouchableOpacity
        onPress={onUndo}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={`Undo: ${message}`}
        testID="undo-banner.undo"
      >
        <Text style={styles.action}>Undo</Text>
      </TouchableOpacity>
    </View>
  );
}

// Inverted colours so it reads as a transient overlay in both themes.
const createStyles = (theme: Theme) => ({
  banner: {
    position: 'absolute' as const,
    left: 16,
    right: 88, // clear of the floating action button
    bottom: 24,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    gap: 12,
    backgroundColor: theme.colors.textPrimary,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 16,
    ...theme.shadows.sm,
  },
  message: { flex: 1, fontSize: 15, color: theme.colors.background },
  action: { fontSize: 15, fontWeight: '700' as const, color: theme.colors.background },
});
